/**
 * Sublishop - cut-out label add-to-cart notice
 * -------------------------------------------
 * Some discounted garments carry a third-party woven neck label the customer
 * has to cut out. That is stated on the product page and on the cart line, but
 * a trade buyer bulk-ordering can skim straight past both. This puts it in
 * front of them at the moment they commit.
 *
 * Behaviour
 *   - Fires AFTER a successful add to cart, never blocks the add.
 *   - Only for products carrying the tag below.
 *   - Shown ONCE per customer, then never again (see STORAGE).
 *
 * The "seen" flag is set when the modal is displayed, not per add-to-cart, so
 * a customer adding four sizes of the same garment sees it on the first add
 * only. This is deliberate - the stock is a one-off clearance line.
 *
 * This wraps window.fetch on top of pack-stock-guard.js, so it only sees adds
 * that the stock guard actually allowed through.
 *
 * The wording here is deliberately shorter than the product-page warning in
 * snippets/cut-out-label-warning.liquid - this is a confirmation at the point
 * of commitment, not the full explanation. Keep the two consistent in tone if
 * you edit either.
 */
(function () {
  'use strict';

  var TAG = 'cut-out-label';
  var KEY = 'sublishop:cutout-label-seen';

  // localStorage = once ever, per browser. Swap to window.sessionStorage for
  // once per visit, or set to null to show on every add.
  var STORAGE = window.localStorage;

  var COPY = {
    heading: 'Remember to cut the label out',
    body: 'The item you just added carries a "Fun Cases" woven neck label instead of our plain unbranded one. That is why it is discounted. The garment itself is unchanged - same fabric, polyester content, sizing and print results - but you will need to snip the label out before you print or resell it.',
    button: 'Got it'
  };

  var nativeFetch = window.fetch.bind(window);
  var tagCache = new Map();
  var open = false;

  function rootPath() {
    var r = (window.theme && theme.routes && theme.routes.cart) || '/cart';
    return r.replace(/\/cart$/, '');
  }

  function alreadySeen() {
    if (!STORAGE) return false;
    try { return STORAGE.getItem(KEY) === '1'; } catch (e) { return false; }
  }

  function markSeen() {
    if (!STORAGE) return;
    try { STORAGE.setItem(KEY, '1'); } catch (e) { /* private mode */ }
  }

  /** Does this product carry the tag? Cached, and fails closed (no popup). */
  function isFlagged(handle) {
    if (!handle) return Promise.resolve(false);
    if (tagCache.has(handle)) return tagCache.get(handle);

    var p = nativeFetch(rootPath() + '/products/' + handle + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d || !d.tags) return false;
        return d.tags.some(function (t) { return String(t).toLowerCase() === TAG; });
      })
      .catch(function () { return false; });

    tagCache.set(handle, p);
    return p;
  }

  function handlesFrom(data) {
    if (!data) return [];
    var items = Array.isArray(data.items) ? data.items : [data];
    return items.map(function (i) { return i && i.handle; }).filter(Boolean);
  }

  /* ------------------------------------------------------------------ modal */

  function buildModal() {
    var overlay = document.createElement('div');
    overlay.className = 'cutout-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'cutout-modal-heading');
    overlay.innerHTML =
      '<div class="cutout-modal__box">' +
        '<div class="cutout-modal__icon" aria-hidden="true">!</div>' +
        '<h2 class="cutout-modal__heading" id="cutout-modal-heading"></h2>' +
        '<p class="cutout-modal__body"></p>' +
        '<button type="button" class="cutout-modal__btn"></button>' +
      '</div>';
    overlay.querySelector('.cutout-modal__heading').textContent = COPY.heading;
    overlay.querySelector('.cutout-modal__body').textContent = COPY.body;
    overlay.querySelector('.cutout-modal__btn').textContent = COPY.button;
    return overlay;
  }

  function show() {
    if (open) return;
    open = true;

    var overlay = buildModal();
    var lastFocus = document.activeElement;

    function close() {
      open = false;
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    function onKey(e) {
      if (e.key === 'Escape') close();
      // Only one focusable element, so keep Tab inside the dialog.
      if (e.key === 'Tab') { e.preventDefault(); btn.focus(); }
    }

    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    document.addEventListener('keydown', onKey);

    document.body.appendChild(overlay);
    var btn = overlay.querySelector('.cutout-modal__btn');
    btn.addEventListener('click', close);
    btn.focus();

    markSeen();
  }

  /* ------------------------------------------------------------- interceptor */

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isAdd = /\/cart\/add(\.js)?($|\?)/.test(url);

    var result = nativeFetch(input, init);
    if (!isAdd || alreadySeen()) return result;

    return result.then(function (response) {
      if (!response.ok) return response;   // blocked by the stock guard

      var clone;
      try { clone = response.clone(); } catch (e) { return response; }

      clone.json()
        .then(function (data) {
          var handles = handlesFrom(data);
          if (!handles.length) return;
          return Promise.all(handles.map(isFlagged)).then(function (flags) {
            if (flags.some(Boolean)) {
              // Let the theme's cart drawer settle first so we land on top.
              setTimeout(show, 350);
            }
          });
        })
        .catch(function () { /* never let this break a cart add */ });

      return response;
    });
  };
})();
